import { type ReactElement } from 'react';
import { ClipboardList } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { EmptyState } from '@/ui/EmptyState';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { MobileCardList, type ItemDoCard } from '@/ui/MobileCardList';
import { StatusBadge } from '@/ui/StatusBadge';

import {
  ROTULO_DO_STATUS,
  VARIANTE_DO_STATUS,
  caminhoDaOrdem,
  numeroDaOs,
  type OrdemDoCredenciado,
} from './contrato';
import { TEXTO_SEM_TERMO_ATIVO, useOrdensPorTermo } from './dados';

const CLASSE_DO_LINK =
  'text-link focus-visible:ring-ring rounded font-mono font-medium hover:underline focus:outline-hidden focus-visible:ring-2';

function Vazio({ descricao }: { descricao: string }): ReactElement {
  return (
    <EmptyState
      icon={ClipboardList}
      title="Sem OS vinculadas"
      description={descricao}
      className="py-6"
    />
  );
}

interface OsVinculadasDoTermoProps {
  termoId: string;
  /** Nome acessível da tabela, ex.: "Ordens de Serviço do termo 2030/0000007". */
  rotulo: string;
}

/**
 * As Ordens de Serviço de um Termo de Adesão: a sub-tabela do cartão do termo
 * em Contratos e Termos e a aba "OS vinculadas" do detalhe.
 *
 * O número da OS é um `<Link>` de verdade para o detalhe, e a linha inteira
 * leva ao mesmo lugar (regra de interface 9). Com `linhaComElementoInterativo`
 * a linha deixa de ser `role="button"`, para não aninhar dois controles
 * (axe, nested-interactive); o teclado chega pelo link. Não há coluna de ação:
 * o "Solicitar" do protótipo não executa nada aqui (regras 7 e 10).
 */
export function OsVinculadasDoTermo({ termoId, rotulo }: OsVinculadasDoTermoProps): ReactElement {
  const navigate = useNavigate();
  const consulta = useOrdensPorTermo(termoId);
  const ordens = consulta.dados ?? [];
  const abrir = (ordem: OrdemDoCredenciado) => navigate(caminhoDaOrdem(ordem.id));

  if (consulta.semTermoAtivo) {
    return <Vazio descricao={TEXTO_SEM_TERMO_ATIVO} />;
  }

  const status = (ordem: OrdemDoCredenciado) => (
    <StatusBadge status={VARIANTE_DO_STATUS[ordem.status]} label={ROTULO_DO_STATUS[ordem.status]} />
  );

  const colunas: DataTableColumn<OrdemDoCredenciado>[] = [
    {
      id: 'os',
      header: 'OS',
      cell: (ordem) => (
        <Link to={caminhoDaOrdem(ordem.id)} className={CLASSE_DO_LINK}>
          {numeroDaOs(ordem)}
        </Link>
      ),
    },
    { id: 'status', header: 'Status', cell: status },
    { id: 'veiculo', header: 'Veículo', cell: (ordem) => ordem.placa ?? '—' },
    { id: 'condutor', header: 'Condutor', cell: (ordem) => ordem.condutor ?? '—' },
    { id: 'monitor', header: 'Monitor', cell: (ordem) => ordem.monitor ?? '—' },
  ];

  const cartoes: ItemDoCard[] = ordens.map((ordem) => ({
    id: ordem.id,
    // Título em texto, sem link: o cartão inteiro é o alvo, e o caminho de
    // teclado dele é do próprio `MobileCardList` (P6 dá `role="button"` e
    // Enter/Espaço ao cartão com `onClick`). Link dentro dele aninharia dois
    // controles (axe, nested-interactive).
    title: <span className="font-mono">{numeroDaOs(ordem)}</span>,
    badges: status(ordem),
    fields: [
      { label: 'Veículo', value: ordem.placa ?? '—' },
      { label: 'Condutor', value: ordem.condutor ?? '—' },
      { label: 'Monitor', value: ordem.monitor ?? '—', full: true },
    ],
    onClick: () => abrir(ordem),
  }));

  return (
    <EstadoDaTela
      carregando={consulta.carregando}
      erro={consulta.erro}
      aoTentarDeNovo={consulta.tentarDeNovo}
      descricao="as ordens de serviço"
      esqueleto={{ colunas: colunas.length }}
    >
      {ordens.length === 0 ? (
        <Vazio descricao="Nenhuma Ordem de Serviço ativa neste termo. A OS é emitida e ativada pelo DTP." />
      ) : (
        <>
          <div className="sm:hidden">
            <MobileCardList items={cartoes} />
          </div>
          <div className="hidden min-w-0 sm:block">
            <DataTable
              columns={colunas}
              data={ordens}
              onRowClick={abrir}
              linhaComElementoInterativo
              minWidth="560px"
              ariaLabel={rotulo}
            />
          </div>
        </>
      )}
    </EstadoDaTela>
  );
}

export default OsVinculadasDoTermo;
